/**
 * Copyright (c) 2017-present, Tomaruba, Inc.
 * All rights reserved.
 *
 */
import './MonthlyReservations.scss'
import React from 'react'
import _ from 'lodash'
import { List } from 'immutable'
import r from '../../../util/Renderer'
import { getMonthlyDates } from '../../../util/DateProcessing'
import { OtaLogo } from '../../atoms'
import { recomposeReservations, calculateMaxDuplicationCount, getReservationsByDate, isSameDate } from './util/MonthlyReservationsUtil'
import ReservationAction from '../../../action/reservation'

export default function MonthlyReservations(props) {
  const { monthly, match, rooms } = props.children

  const today = monthly.today
  const date = monthly.date
  const reservations = monthly.reservations
  const eventSchedules = monthly.event_schedules
  const dates = getMonthlyDates(date)
  // 月末日
  const endOfMonth = _.last(dates)

  const reservationArray = List(reservations).toJS()
  const eventScheduleArray = List(eventSchedules).toJS()
  // 使いやすい構造に作り変えておく
  const schedules = recomposeReservations(reservationArray, eventScheduleArray, date)

  // Immutable.Listのままだと表示が崩れる
  const roomArray = List(rooms).toJS()
  const _rooms = roomArray.map(room => {
    return { id: room.id, name: room.name }
  })

  const dateParam = match.params.date

  return (
    <div className={`MonthlyView MonthlyView--${endOfMonth.numberOfDate}`}>
      <DateRow dates={dates} today={today} />
      {_rooms.map(room => {
        const schedulesByRoomId = schedules[room.id] || null
        // ダブルブッキングなどで同じ日付に予約されることがあるので行の高さを事前に計算しておく
        // 最大9行までCSS側で対応している。これ以上増えるのであれば動的にCSSを設定するようにしなければいけない
        const lines = calculateMaxDuplicationCount(schedulesByRoomId)
        return <Row key={room.id} dates={dates} room={room} schedules={schedulesByRoomId} lines={lines} today={today} dateParam={dateParam} endOfMonth={endOfMonth.numberOfDate} />
      })}
    </div>
  )
}

function DateRow(props) {
  const { dates, today } = props
  return (
    <div className={`MonthlyView__Row MonthlyView__Row__Dates MonthlyView__Row__Lines--1`}>
      <Header>&nbsp;</Header>
      {dates.map(date => (
        <Cell key={date.numberOfDate}>
          <div className="MonthlyView__Row__Cell__Date">
            {r.renderIf(isSameDate(today, date.date))(<div className="today" />)}
            {date.numberOfDate}
            <span className="MonthlyView__Row__Cell__Day">({date.dayOfWeek})</span>
          </div>
        </Cell>
      ))}
    </div>
  )
}

function Row(props) {
  const { dates, room, schedules, lines, dateParam, endOfMonth } = props

  if (!schedules) return <EmptyRow dates={dates} room={room} lines={lines} />

  return (
    <div className={`MonthlyView__Row MonthlyView__Row__Lines--${lines}`}>
      <Header lines={lines}>
        <span className="MonthlyView__Row__Header__Name">{room.name}</span>
      </Header>
      {dates.map(date => (
        <Cell key={`${room.id}-${date.numberOfDate}`}>
          <Stays
            key={`${room.id}-${date.numberOfDate}-stay`}
            date={date.numberOfDate}
            schedules={getReservationsByDate(date.numberOfDate, schedules, endOfMonth)}
            dateParam={dateParam}
            endOfMonth={endOfMonth}
          />
        </Cell>
      ))}
    </div>
  )
}

// 予約がない物件も行だけ表示する
function EmptyRow(props) {
  const { dates, room, lines } = props
  return (
    <div className={`MonthlyView__Row MonthlyView__Row__Lines--${lines}`}>
      <Header lines={lines}>
        <span className="MonthlyView__Row__Header__Name">{room.name}</span>
      </Header>
      {dates.map(date => (
        <Cell key={`${room.id}-${date.numberOfDate}`}>
          <div key={`${room.id}-${date.numberOfDate}-stay`} className={`MonthlyView__Row__Stay__Container`} />
        </Cell>
      ))}
    </div>
  )
}

function Header(props) {
  const lines = props.lines || 1

  return <div className={`MonthlyView__Row__Header MonthlyView__Row__Header__Lines--${lines}`}>{props.children}</div>
}

function Cell(props) {
  const className = props.className || ''
  return <div className={`MonthlyView__Row__Cell ${className}`}>{props.children}</div>
}

function getAdditionalCssForStay(schedule, endOfMonth, additional = 0) {
  const from = schedule.from
  const to = schedule.to
  let css = ''
  if (from === 0) {
    css += `MonthlyView__Row__Stay__FromLastMonth MonthlyView__Row__Stay__FromLastMonth--${schedule.rowIndex}`
  }

  const stayingDays = to - from + additional

  if (to === endOfMonth + 1) {
    css += ` MonthlyView__Row__Stay__ToNextMonth--${stayingDays}`
  } else {
    css += ` MonthlyView__Row__Stay--${stayingDays}`
  }

  return css
}

// 宿泊予約の表示
function Stays(props) {
  // ダブルブッキングで複数件登録される場合がある
  const { schedules, dateParam, endOfMonth } = props

  const maxRowIndex = schedules.reduce((acc, cur) => {
    const idx = cur.rowIndex
    return acc > idx ? acc : idx
  }, 0)

  const rows = _.range(0, maxRowIndex + 1)
  const grouped = _.groupBy(schedules, 'rowIndex')

  return (
    <div className={`MonthlyView__Row__Stay__Container`}>
      {rows.map(index => {
        const items = grouped[index] || []
        if (!_.isEmpty(items)) {
          return items.map(item => {
            const status = item.schedule.status || 'unknown'

            // 月またぎ&&ダブルブッキング状態の場合に月またぎしている予約とダブルブッキングしている予約が
            // 重なってしまうので空の要素を足して表示上の微調整を行う必要がある
            const lastRowIndex = index - 1
            const last = _.head(grouped[lastRowIndex]) || null
            const isAddEmptyRow = last !== null ? last.from == 0 && last.to > 1 && item.from == 1 : false

            if (item.schedule.is_reservation) {
              const css = getAdditionalCssForStay(item, endOfMonth)
              return (
                <React.Fragment key={item.schedule.id}>
                  {r.renderIf(isAddEmptyRow)(<div className={`MonthlyView__Row__Stay`}>&nbsp;</div>)}
                  <div
                    className={`MonthlyView__Row__Stay MonthlyView__Row__Stay__Status--${status} ${css}`}
                    onClick={e => ReservationAction.showDetailView(item.schedule.id, `/calendar/${dateParam}/${item.schedule.id}/detail`)}
                  >
                    <OtaLogo className="MonthlyView__Row__Stay__SiteReferer" value={item.schedule.site_referer} />
                    <span className="MonthlyView__Row__Stay__Name">{item.schedule.name}</span>
                  </div>
                </React.Fragment>
              )
            }

            const css = getAdditionalCssForStay(item, endOfMonth, 1)
            return (
              <React.Fragment key={item.schedule.id}>
                {r.renderIf(isAddEmptyRow)(<div className={`MonthlyView__Row__Stay`}>&nbsp;</div>)}
                <div className={`MonthlyView__Row__Stay MonthlyView__Row__Stay__Status--cleaning ${css}`}>
                  <span className="MonthlyView__Row__Stay__Name">
                    <a target="_blank" rel="noopener noreferrer" href={`/event_schedule/${item.schedule.id}/edit`}>
                      {item.schedule.title}
                    </a>
                  </span>
                </div>
              </React.Fragment>
            )
          })
        }

        return (
          <React.Fragment key={index}>
            <div className={`MonthlyView__Row__Stay`}>&nbsp;</div>
          </React.Fragment>
        )
      })}
    </div>
  )
}
